import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject, timer } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TopBarComponent } from './components/top-bar/top-bar.component';
import { TourComponent } from './components/tour/tour.component';
import { BadgeComponent, LogoComponent, StatusDotComponent } from './components/shared/ui/ui';
import { AuthService } from './services/auth.service';
import { OnboardingService } from './services/onboarding.service';
import { TourService } from './services/tour.service';

/** Lets the first screen paint before the tour dims it. */
const TOUR_START_DELAY_MS = 800;

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TopBarComponent,
    TourComponent,
    StatusDotComponent,
    BadgeComponent,
    LogoComponent],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit, OnDestroy {

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private onboardingService = inject(OnboardingService);
  private tourService = inject(TourService);
  private breakpointObserver = inject(BreakpointObserver);
  private destroy$ = new Subject<void>();

  @ViewChild('sidenav') sidenav!: MatSidenav;

  /** No widget of the active chatbot can load anywhere: none exists, or none is live with an address. */
  widgetNeedsAttention = false;
  isMobile = false;
  /**
   * Material's own default for each mode, spelled out because a bound value replaces it — except
   * while the tour runs, when the drawer opening on mobile would pull focus out of the tour card.
   */
  sidenavAutoFocus: 'first-tabbable' | 'dialog' | false = 'dialog';
  private touring = false;
  /** The tour opened the mobile drawer, so the tour closes it again. */
  private tourOpenedSidenav = false;
  readonly year = new Date().getFullYear();

  ngOnInit() {
    this.route.data.subscribe(data => {
      if (data["user"]) {
        this.authService.userDetails = data["user"];
      }
    });

    // Readiness of the active chatbot's widgets. An unknown state (still loading, or the list
    // failed) is not flagged: the dot claims something is wrong, so it needs to know.
    this.onboardingService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.widgetNeedsAttention = !state.loading && !state.channelsError && !state.widgetLive;
      });

    // Load onboarding readiness once the authenticated layout is entered.
    this.onboardingService.refresh();

    // Detect mobile breakpoint
    this.breakpointObserver
      .observe('(max-width: 768px)')
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isMobile = result.matches;
        this.updateSidenavAutoFocus();
        if (this.sidenav) {
          if (this.isMobile) {
            this.sidenav.close();
          } else {
            this.sidenav.open();
          }
        }
      });

    // Auto-close sidenav on navigation when mobile
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.isMobile && this.sidenav) {
          this.sidenav.close();
        }
      });

    // On mobile the nav is a closed drawer; open it while the tour points at something inside.
    this.tourService.view$
      .pipe(takeUntil(this.destroy$))
      .subscribe(view => {
        this.touring = !!view;
        this.updateSidenavAutoFocus();
        if (!this.isMobile || !this.sidenav) return;

        if (view?.step.inSidenav) {
          if (!this.sidenav.opened) {
            this.tourOpenedSidenav = true;
            this.sidenav.open();
          }
        } else if (this.tourOpenedSidenav) {
          this.tourOpenedSidenav = false;
          this.sidenav.close();
        }
      });

    timer(TOUR_START_DELAY_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.tourService.startIfNew());
  }

  ngOnDestroy() {
    this.tourService.close();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateSidenavAutoFocus() {
    this.sidenavAutoFocus = this.touring ? false : this.isMobile ? 'first-tabbable' : 'dialog';
  }

}
